// src/hooks/useFolderNavigation.js
import { useCallback, useEffect, useMemo } from 'react';
import { useFileContext } from '../contexts/FileContext';
import * as fileService from '../services/file.service';

export function useFolderNavigation() {
  const { 
    setCurrentFolder, 
    setCurrentPath, 
    currentPath,
    setLoading,
    resetController,
    clearSelection
  } = useFileContext();

const getBucketContent = useCallback(async (page = 1) => {

  
  setLoading(true);
  try {
    const result = await fileService.getBucketContent(page);
    const sortedItems = sortItemsWithFoldersOnTop(result.items);
    
    setCurrentFolder({
      name: "All Files",
      type: "folder",
      children: sortedItems
    });
    setCurrentPath([]);
    return result;
  } catch (error) {
    console.error('Error loading bucket content:', error);
    throw error;
  } finally {
    setLoading(false);
  }
}, [setCurrentFolder, setCurrentPath, setLoading]);

  const navigateToFolder = useCallback(async (folderPath) => {
    if (!folderPath && folderPath !== '') return;
    setLoading(true);
    clearSelection();
    try {
      let pathSegments = [];
      let targetFolder;
      if (!folderPath || folderPath === "") {
        await getBucketContent();
        return;
      }
      if (typeof folderPath === 'string') {
        pathSegments = folderPath.split("/").filter(Boolean);
      } else if (Array.isArray(folderPath)) {
        pathSegments = folderPath;
      }
      targetFolder = Array.isArray(folderPath) ? folderPath.join('/') : folderPath;
      const folderContents = await fileService.fetchFolderContents(targetFolder, "files");
      const sortedItems = sortItemsWithFoldersOnTop(folderContents);
      setCurrentFolder({
        name: pathSegments[pathSegments.length - 1] || "All Files",
        type: "folder",
        children: sortedItems || [],
      });
      setCurrentPath(pathSegments);
      updateBrowserHistory(pathSegments);
      return folderContents;
    } catch (error) {
      console.error("Error navigating to folder:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [clearSelection, getBucketContent, setCurrentFolder, setCurrentPath, setLoading]);

  const navigateUp = useCallback(() => {
    if (currentPath.length === 0) return;
    const parentPath = currentPath.slice(0, -1);
    if (parentPath.length === 0) {
      getBucketContent();
    } else {
      navigateToFolder(parentPath.join('/'));
    }
  }, [currentPath, getBucketContent, navigateToFolder]);

const refreshFolder = useCallback(async () => {

  
  resetController();
  if (currentPath.length === 0) {
    await getBucketContent();
  } else {
    await navigateToFolder(currentPath);
  }
}, [currentPath, getBucketContent, navigateToFolder, resetController]);

  const sortItemsWithFoldersOnTop = (items) => {
    const folders = items.filter((item) => item.type === "folder");
    const files = items.filter((item) => item.type !== "folder");
    folders.sort((a, b) => a.name.localeCompare(b.name));
    files.sort((a, b) => a.name.localeCompare(b.name));
    return [...folders, ...files];
  };

  const updateBrowserHistory = (pathSegments) => {
    window.history.pushState({
      folderPath: pathSegments,
      isFolder: true
    }, '', window.location.href);
  };

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.isFolder) {
        if (Array.isArray(event.state.folderPath)) {
          const path = event.state.folderPath.length === 0 ? '' : event.state.folderPath.join('/');
          navigateToFolder(path);
        } else {
          getBucketContent();
        }
        event.preventDefault();
        return false;
      }
    };
    window.addEventListener('popstate', handlePopState);
    if (!window.history.state || !window.history.state.folderPath) {
      window.history.replaceState({ 
        folderPath: [], 
        isFolder: true 
      }, '', window.location.href);
    }
    return () => {
      window.addEventListener('popstate', handlePopState);
    };
  }, [getBucketContent, navigateToFolder]);

  return useMemo(() => ({
    navigateToFolder,
    navigateUp,
    refreshFolder,
    getBucketContent
  }), [navigateToFolder, navigateUp, refreshFolder, getBucketContent]);
}